import { useEffect, useMemo, useState, type MouseEvent } from 'react';

import { css, keyframes } from '@compiled/react';
import {
	isRichTextValueConsideredEmpty,
	type ParameterRichTextValue,
	type RichTextNode,
} from '@uniformdev/richtext';
import { defineMessages, useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { Button } from '@/libraries/design/components/button/src/button';
import { Image } from '@/libraries/design/components/image/src/image';
import { Lozenge } from '@/libraries/design/components/lozenge/src/lozenge.tsx';
import { wacToken, ColumnLayout } from '@/libraries/design/foundations';
import { RichTextComponent } from '@/libraries/design/utils';

const DEFAULT_DOWNLOADS_API_BASE =
	'https://dc-downloads-admin.services.atlassian.com/api/1.0/public/downloads/binaryDownloads';
const CURRENT_FEED_TYPE = 'current';
const ARCHIVED_FEED_TYPE = 'archived';
const EAP_FEED_TYPE = 'eap';
const DEFAULT_POST_DOWNLOAD_IMAGE_URL = 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12P6RV.svg';

const PRODUCT_DISPLAY_NAMES: Record<string, string> = {
	bamboo: 'Bamboo',
	bitbucket: 'Bitbucket',
	'bitbucket-eap': 'Bitbucket',
	clover: 'Clover',
	confluence: 'Confluence',
	'confluence-eap': 'Confluence',
	crowd: 'Crowd',
	'crowd-eap': 'Crowd',
	crucible: 'Crucible',
	fisheye: 'Fisheye',
	'fisheye-eap': 'Fisheye',
	jira: 'Jira',
	'jira-core': 'Jira Core',
	'jira-combined': 'Jira Software',
	'jira-eap': 'Jira',
	'jira-software': 'Jira Software',
	'jira-servicedesk': 'Jira Service Management',
	mesh: 'Bitbucket Mesh',
	stash: 'Bitbucket',
};

const DATA_CENTER_ONLY_MINIMUM_VERSION_BY_PRODUCT: Record<
	string,
	{ majorVersion: number; minorVersion: number; microVersion: number }
> = {
	bamboo: {
		majorVersion: 9,
		minorVersion: 5,
		microVersion: 0,
	},
	confluence: {
		majorVersion: 8,
		minorVersion: 6,
		microVersion: 0,
	},
	'jira-combined': {
		majorVersion: 9,
		minorVersion: 13,
		microVersion: 0,
	},
	'jira-software': {
		majorVersion: 9,
		minorVersion: 13,
		microVersion: 0,
	},
	'jira-servicedesk': {
		majorVersion: 5,
		minorVersion: 13,
		microVersion: 0,
	},
	stash: {
		majorVersion: 8,
		minorVersion: 15,
		microVersion: 0,
	},
};

type EditionDisplay = 'standard' | 'enterprise' | 'both';
type Image =
	| string
	| { url?: string; path?: string; src?: string; assetJson?: { imageUrl?: string } };
type RichText = ParameterRichTextValue | RichTextNode | Record<string, unknown> | undefined;

const messages = defineMessages({
	loading: {
		id: 'uniform.downloadArchives.loading',
		defaultMessage: 'Loading downloads…',
	},
	noDownloads: {
		id: 'uniform.downloadArchives.noDownloads',
		defaultMessage: 'No downloads available at this time - check back later.',
	},
	latestRelease: {
		id: 'uniform.downloadArchives.latestRelease',
		defaultMessage: 'Latest release',
	},
	eapDownload: {
		id: 'uniform.downloadArchives.eapDownload',
		defaultMessage: 'Early Access Program download',
	},
	eapNotForProductionHeading: {
		id: 'uniform.downloadArchives.eapNotForProductionHeading',
		defaultMessage: 'Not for production',
	},
	eapNotForProductionDescription: {
		id: 'uniform.downloadArchives.eapNotForProductionDescription',
		defaultMessage:
			'EAP releases should not be used in production environments as they are not officially supported.',
	},
	enterpriseRelease: {
		id: 'uniform.downloadArchives.enterpriseRelease',
		defaultMessage: 'Enterprise release',
	},
	latestText: {
		id: 'uniform.downloadArchives.latestText',
		defaultMessage: 'Latest',
	},
	dataCenterOnly: {
		id: 'uniform.downloadArchives.dataCenterOnly',
		defaultMessage: 'Data Center Only',
	},
	download: {
		id: 'uniform.downloadArchives.download',
		defaultMessage: 'Download',
	},
	releaseNotes: {
		id: 'uniform.downloadArchives.releaseNotes',
		defaultMessage: 'Release notes',
	},
	viewAllVersions: {
		id: 'uniform.downloadArchives.viewAllVersions',
		defaultMessage: 'View all versions',
	},
	showDetails: {
		id: 'uniform.downloadArchives.showDetails',
		defaultMessage: 'Show details',
	},
	hideDetails: {
		id: 'uniform.downloadArchives.hideDetails',
		defaultMessage: 'Hide details',
	},
	downloadHeading: {
		id: 'uniform.downloadArchives.downloadHeading',
		defaultMessage: 'Download {productName} {version}',
	},
	downloadSelectLabel: {
		id: 'uniform.downloadArchives.downloadSelectLabel',
		defaultMessage: 'Choose your download',
	},
	downloadAgreementDescription: {
		id: 'uniform.downloadArchives.downloadAgreementDescription',
		defaultMessage:
			'To continue downloading this product you must read and agree to the Atlassian Software License Agreement and Privacy Policy.',
	},
	softwareLicenseAgreement: {
		id: 'uniform.downloadArchives.softwareLicenseAgreement',
		defaultMessage: 'Atlassian Software License Agreement',
	},
	privacyPolicy: {
		id: 'uniform.downloadArchives.privacyPolicy',
		defaultMessage: 'Privacy Policy',
	},
	agreementCheckbox: {
		id: 'uniform.downloadArchives.agreementCheckbox',
		defaultMessage: 'I agree to the {licenseAgreement} and {privacyPolicy}.',
	},
	chooseAnother: {
		id: 'uniform.downloadArchives.chooseAnother',
		defaultMessage: 'Choose another version',
	},
	checksum: {
		id: 'uniform.downloadArchives.checksum',
		defaultMessage: 'Verify your download using the SHA256 checksum',
	},
	confirmationHeaderText: {
		id: 'uniform.downloadArchives.confirmationHeaderText',
		defaultMessage: 'Thanks for downloading',
	},
	gettingStartedHeading: {
		id: 'uniform.downloadArchives.gettingStartedHeading',
		defaultMessage: 'Getting started',
	},
	gettingStartedStepLocate: {
		id: 'uniform.downloadArchives.gettingStartedStepLocate',
		defaultMessage: 'Locate the installation file',
	},
	gettingStartedStepRun: {
		id: 'uniform.downloadArchives.gettingStartedStepRun',
		defaultMessage: 'Run the installer',
	},
	resourcesHeading: {
		id: 'uniform.downloadArchives.resourcesHeading',
		defaultMessage: 'Resources',
	},
	installerCtaText: {
		id: 'uniform.downloadArchives.installerCtaText',
		defaultMessage: 'Download installer again',
	},
	enterpriseLabelText: {
		id: 'uniform.downloadArchives.enterpriseLabelText',
		defaultMessage: 'Long Term Support',
	},
	enterpriseHelpHeading: {
		id: 'uniform.downloadArchives.enterpriseHelpHeading',
		defaultMessage: 'Long Term Support Releases',
	},
	enterpriseHelpContent: {
		id: 'uniform.downloadArchives.enterpriseHelpContent',
		defaultMessage:
			'These are designated feature releases that will continue to receive backported security updates and critical bug fixes throughout the standard 2-year support window. This allows you to get the maximum value from our continuous product development without strictly keeping up with the frequent release cadence.',
	},
	enterpriseHelpButtonAriaLabel: {
		id: 'uniform.downloadArchives.enterpriseHelpButtonAriaLabel',
		defaultMessage: 'Learn about Long Term Support releases',
	},
	enterpriseHelpCloseAriaLabel: {
		id: 'uniform.downloadArchives.enterpriseHelpCloseAriaLabel',
		defaultMessage: 'Close Long Term Support information',
	},
});

interface DownloadFeedVersion {
	date?: string;
	name?: string;
	releaseNotesURL?: string;
	upgradeNotesURL?: string;
	product?: {
		key?: string;
		name?: string;
	};
}

interface DownloadFeedLabel {
	labelName?: string;
}

interface DownloadFeedItem {
	displayName?: string;
	edition?: string;
	name?: string;
	selected?: string;
	show?: string;
	size?: string;
	zipURL?: string;
	version?: DownloadFeedVersion | string;
	downloadLabels?: DownloadFeedLabel[];
}

interface ProductDownloadOption {
	description: string;
	displayName: string;
	fileName: string;
	productName?: string;
	platform: string;
	selected: boolean;
	show: boolean;
	size: string;
	url: string;
}

interface ProductVersion {
	date: string;
	edition: string;
	longVersion: string;
	majorVersion: string;
	minorVersion: string;
	microVersion: string;
	productName: string;
	released: Date;
	releaseNotes?: string;
	downloads: ProductDownloadOption[];
}

interface MajorVersionGroup {
	majorVersion: string;
	minorVersions: MinorVersionGroup[];
}

interface MinorVersionGroup {
	minorVersion: string;
	versions: ProductVersion[];
}

export interface UniformDownloadArchivesProps {
	productKey: string;
	editionDisplay?: EditionDisplay;
	enterpriseLabelText?: string;
	showMostPopularDownloadFirst?: boolean;
	filetype?: string;
	showChecksum?: boolean;
	downloadSelectLabelText?: string;
	confirmationHeaderText?: string;
	image?: Image;
	imageAltText?: string;
	gettingStartedHeading?: string;
	gettingStartedRichText?: RichText;
	resourcesRichText?: RichText;
	downloadsApiBase?: string;
	errorMessage?: string;
}

const normalizeHttpsUrl = (url: string): string => url.replace(/^http:/i, 'https:');

const getFileName = (filePath: string): string => {
	try {
		return filePath.substring(filePath.lastIndexOf('/') + 1) || 'Unknown';
	} catch {
		return 'Unknown';
	}
};

const formatReleaseDate = (date: Date): string => {
	const day = String(date.getUTCDate()).padStart(2, '0');
	const month = date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
	const year = date.getUTCFullYear();
	return `${day}-${month}-${year}`;
};

const getProductKey = (item: DownloadFeedItem): string | undefined =>
	typeof item.version === 'object' ? item.version.product?.key : undefined;

const getVersionName = (item: DownloadFeedItem, productType?: string): string => {
	const feedVersionName = typeof item.version === 'object' ? item.version.name : undefined;
	const versionName = feedVersionName ?? item.name ?? '';
	const numericVersion = /\d+(?:\.\d+)+(?:[-\w.]*)?/.exec(versionName)?.[0];

	if ((productType === 'clover' || getProductKey(item) === 'clover') && numericVersion) {
		return numericVersion;
	}

	return feedVersionName ?? numericVersion ?? item.name ?? '0.0.0';
};

const getCloverEnvironmentNameFromText = (value?: string): string | undefined =>
	value?.match(/(?:Clover\s+)?for\s+([^-]+?)(?:\s+-|$)/i)?.[1]?.trim();

const getCloverEnvironmentName = (item: DownloadFeedItem): string | undefined =>
	getCloverEnvironmentNameFromText(item.name);

const formatCloverDownloadLabel = ({
	environmentName,
	platformLabel,
}: {
	environmentName?: string;
	platformLabel: string;
}): string | undefined => {
	if (!environmentName) {
		return undefined;
	}

	if (!platformLabel || platformLabel === environmentName) {
		return environmentName;
	}

	if (platformLabel.startsWith(`${environmentName} —`)) {
		return platformLabel;
	}

	return `${environmentName} — ${platformLabel}`;
};

const getDownloadDisplayName = (item: DownloadFeedItem, fallback: string): string => {
	const cloverLabel = formatCloverDownloadLabel({
		environmentName: getCloverEnvironmentName(item),
		platformLabel: item.displayName || fallback,
	});
	if (cloverLabel) {
		return cloverLabel;
	}

	if (item.displayName) {
		return item.displayName;
	}

	return fallback;
};

const splitVersion = (version: string) => {
	const [majorVersion = version, minor = '0', microVersion = '0'] = version.split('.');
	return {
		majorVersion,
		minorVersion: `${majorVersion}.${minor.replace(/-.*/, '')}`,
		microVersion,
	};
};

const numericVersionParts = (version: string): number[] =>
	version.split(/[.-]/).map((part) => {
		const parsed = Number.parseInt(part, 10);
		return Number.isNaN(parsed) ? 0 : parsed;
	});

const isAtLeastVersion = (
	version: ProductVersion,
	minimumVersion: { majorVersion: number; minorVersion: number; microVersion: number },
): boolean => {
	const majorVersion = Number.parseInt(version.majorVersion, 10);
	const [, minor = '0'] = version.minorVersion.split('.');
	const minorVersion = Number.parseInt(minor, 10);
	const microVersion = Number.parseInt(version.microVersion, 10);
	const normalizedVersion = [majorVersion, minorVersion, microVersion].map((part) =>
		Number.isNaN(part) ? 0 : part,
	);
	const normalizedMinimumVersion = [
		minimumVersion.majorVersion,
		minimumVersion.minorVersion,
		minimumVersion.microVersion,
	];

	for (let index = 0; index < normalizedMinimumVersion.length; index += 1) {
		const versionPart = normalizedVersion[index] ?? 0;
		const minimumVersionPart = normalizedMinimumVersion[index] ?? 0;
		if (versionPart !== minimumVersionPart) {
			return versionPart > minimumVersionPart;
		}
	}

	return true;
};

const compareVersions = (a: string, b: string): number => {
	const aParts = numericVersionParts(a);
	const bParts = numericVersionParts(b);
	const maxLength = Math.max(aParts.length, bParts.length);

	for (let index = 0; index < maxLength; index += 1) {
		const aPart = aParts[index] ?? 0;
		const bPart = bParts[index] ?? 0;
		if (aPart !== bPart) {
			return bPart - aPart;
		}
	}

	return 0;
};

const EAP_PRODUCT_FEED_KEYS: Record<string, string> = {
	'bitbucket-eap': 'bitbucket',
	'confluence-eap': 'confluence',
	'crowd-eap': 'crowd',
	'fisheye-eap': 'fisheye',
	'jira-eap': 'jira',
};

export const isEapProductType = (productType?: string): boolean =>
	Boolean(productType && EAP_PRODUCT_FEED_KEYS[productType]);

const productFeedKeyFor = (productKey: string): string => {
	if (productKey === 'jira-combined') {
		return 'jira-software';
	}

	return EAP_PRODUCT_FEED_KEYS[productKey] ?? productKey;
};

export const buildFeedUrls = ({
	productKey,
	downloadsApiBase,
}: Pick<UniformDownloadArchivesProps, 'productKey' | 'downloadsApiBase'>): string[] => {
	const base = (downloadsApiBase ?? DEFAULT_DOWNLOADS_API_BASE).replace(/\/$/, '');
	const feedProduct = productFeedKeyFor(productKey);

	if (isEapProductType(productKey)) {
		return [`${base}/${feedProduct}/${EAP_FEED_TYPE}`];
	}

	const feeds = [
		`${base}/${feedProduct}/${CURRENT_FEED_TYPE}`,
		`${base}/${feedProduct}/${ARCHIVED_FEED_TYPE}`,
	];

	if (productKey === 'jira-combined') {
		feeds.push(`${base}/jira/${ARCHIVED_FEED_TYPE}`);
	}

	return feeds;
};

const isGenericJiraEapProductName = (value?: string): boolean =>
	/^jira eaps?$/i.test(value?.trim() ?? '');

const getJiraEapProductNameFromText = (value?: string): string | undefined => {
	const normalizedValue = value?.toLowerCase() ?? '';

	if (normalizedValue.includes('jira software') || normalizedValue.includes('jira-software')) {
		return 'Jira Software';
	}

	if (normalizedValue.includes('jira core') || normalizedValue.includes('jira-core')) {
		return 'Jira Core';
	}

	if (
		normalizedValue.includes('jira service management') ||
		normalizedValue.includes('jira-service-management') ||
		normalizedValue.includes('jira servicedesk') ||
		normalizedValue.includes('servicedesk')
	) {
		return 'Jira Service Management';
	}

	return undefined;
};

const getDownloadOptionProductName = (
	item: DownloadFeedItem,
	productKey?: string,
): string | undefined => {
	if (productKey !== 'jira-eap') {
		return undefined;
	}

	const feedProductName = typeof item.version === 'object' ? item.version.product?.name : undefined;

	return (
		getJiraEapProductNameFromText(item.name) ??
		getJiraEapProductNameFromText(item.displayName) ??
		getJiraEapProductNameFromText(item.zipURL) ??
		(isGenericJiraEapProductName(feedProductName) ? undefined : feedProductName)
	);
};

export const normalizeFeedItem = (
	item: DownloadFeedItem,
	productName: string,
	productType?: string,
): ProductVersion | undefined => {
	if (!item.zipURL) {
		return undefined;
	}

	const longVersion = getVersionName(item, productType);
	const released = new Date(typeof item.version === 'object' ? (item.version.date ?? '') : '');
	const zipUrl = normalizeHttpsUrl(item.zipURL);
	const fileName = getFileName(zipUrl);
	const versionParts = splitVersion(longVersion);
	const platform =
		item.downloadLabels
			?.map((label) => label.labelName)
			.filter(Boolean)
			.join(', ') ?? '';
	const fallbackDisplayName = (platform || item.name) ?? fileName;
	const optionProductName = getDownloadOptionProductName(item, productType);

	return {
		...versionParts,
		date: Number.isNaN(released.getTime()) ? '' : formatReleaseDate(released),
		edition: item.edition ?? '',
		longVersion,
		productName,
		released,
		releaseNotes: typeof item.version === 'object' ? item.version.releaseNotesURL : undefined,
		downloads: [
			{
				description: item.name ?? fileName,
				displayName: getDownloadDisplayName(item, fallbackDisplayName),
				fileName,
				productName: optionProductName,
				platform,
				selected: item.selected === 'true',
				show: item.show !== 'false',
				size: item.size ?? '',
				url: zipUrl,
			},
		],
	};
};

export const groupDownloadsByVersion = (downloads: ProductVersion[]): ProductVersion[] => {
	const byVersion = new Map<string, ProductVersion>();

	downloads.forEach((download) => {
		const existing = byVersion.get(download.longVersion);
		if (!existing) {
			byVersion.set(download.longVersion, download);
			return;
		}

		byVersion.set(download.longVersion, {
			...existing,
			downloads: [...existing.downloads, ...download.downloads],
		});
	});

	return Array.from(byVersion.values()).sort((a, b) =>
		compareVersions(a.longVersion, b.longVersion),
	);
};

const isEnterpriseVersion = (download: ProductVersion): boolean =>
	download.edition.toLowerCase() === 'enterprise' ||
	download.downloads.some((option) =>
		`${option.description} ${option.displayName}`.toLowerCase().includes('enterprise'),
	);

const isStandardVersion = (download: ProductVersion): boolean => !isEnterpriseVersion(download);

const shouldShowDataCenterOnlyLozenge = (
	productKey: string | undefined,
	version: ProductVersion,
): boolean => {
	if (!productKey) {
		return false;
	}

	const minimumVersion = DATA_CENTER_ONLY_MINIMUM_VERSION_BY_PRODUCT[productKey];
	return minimumVersion ? isAtLeastVersion(version, minimumVersion) : false;
};

export const shouldShowMajorDataCenterOnlyLozenge = (
	productKey: string | undefined,
	versions: ProductVersion[],
): boolean =>
	versions.length > 0 &&
	versions.every((version) => shouldShowDataCenterOnlyLozenge(productKey, version));

const shouldShowEnterpriseBadge = (version: ProductVersion): boolean =>
	version.edition === 'Enterprise';

const findNewestByEdition = (
	downloads: ProductVersion[],
	editionDisplay: EditionDisplay,
): ProductVersion | undefined => {
	if (editionDisplay === 'enterprise') {
		return downloads.find(isEnterpriseVersion);
	}

	if (editionDisplay === 'both') {
		return downloads[0];
	}

	return downloads.find(isStandardVersion) ?? downloads[0];
};

export const getFeaturedDownloads = (
	downloads: ProductVersion[],
	editionDisplay: EditionDisplay,
): { version: ProductVersion; isEnterprise: boolean }[] => {
	const latestDownload = findNewestByEdition(downloads, editionDisplay);
	const latestEnterpriseDownload = downloads.find(isEnterpriseVersion);

	return [
		...(editionDisplay === 'enterprise'
			? []
			: latestDownload
				? [{ version: latestDownload, isEnterprise: false }]
				: []),
		...(editionDisplay === 'both' || editionDisplay === 'enterprise'
			? latestEnterpriseDownload
				? [{ version: latestEnterpriseDownload, isEnterprise: true }]
				: []
			: []),
	];
};

const getLegacyDownloadOptionLabel = (download: ProductDownloadOption): string | undefined => {
	const description = download.description.toLowerCase();
	const fileName = download.fileName.toLowerCase();
	const searchableText = `${description} ${fileName}`;

	if (searchableText.includes('windows installer') || fileName.endsWith('.exe')) {
		return searchableText.includes('64') || fileName.includes('x64') ? 'Windows 64 Bit' : 'Windows';
	}

	if (searchableText.includes('linux installer') || fileName.endsWith('.bin')) {
		return searchableText.includes('64') || fileName.includes('x64') ? 'Linux 64 Bit' : 'Linux';
	}

	if (searchableText.includes('tar.gz archive') || fileName.endsWith('.tar.gz')) {
		return 'TAR.GZ Archive';
	}

	if (searchableText.includes('zip archive') || fileName.endsWith('.zip')) {
		return 'ZIP Archive';
	}

	return undefined;
};

const formatProductSpecificDownloadLabel = (
	download: ProductDownloadOption,
): string | undefined => {
	if (!download.productName) {
		return undefined;
	}

	if (download.displayName.startsWith(`${download.productName} —`)) {
		return download.displayName;
	}

	return `${download.productName} — ${download.displayName}`;
};

export const getDownloadOptionLabel = (download: ProductDownloadOption): string => {
	const productSpecificLabel = formatProductSpecificDownloadLabel(download);
	if (productSpecificLabel) {
		return productSpecificLabel;
	}

	const cloverLabel = formatCloverDownloadLabel({
		environmentName: getCloverEnvironmentNameFromText(download.description),
		platformLabel: download.displayName,
	});
	if (cloverLabel) {
		return cloverLabel;
	}

	const legacyLabel = getLegacyDownloadOptionLabel(download);
	if (legacyLabel) {
		return !legacyLabel.includes('64 Bit') && download.displayName.includes('64 Bit')
			? download.displayName
			: legacyLabel;
	}

	return download.displayName;
};

const findPreferredDownloadUrl = ({
	version,
	preferredFiletype,
	showMostPopularDownloadFirst,
}: {
	version: ProductVersion;
	preferredFiletype?: string;
	showMostPopularDownloadFirst?: boolean;
}): string => {
	const visibleDownloads = version.downloads.filter((download) => download.show);
	if (!showMostPopularDownloadFirst || !preferredFiletype) {
		return (
			visibleDownloads.find((download) => download.selected)?.url ?? visibleDownloads[0]?.url ?? ''
		);
	}

	const normalizedPreferredFiletype = preferredFiletype.toLowerCase();
	return (
		visibleDownloads.find((download) =>
			`${download.displayName} ${download.description} ${download.fileName}`
				.toLowerCase()
				.includes(normalizedPreferredFiletype),
		)?.url ??
		visibleDownloads.find((download) => download.selected)?.url ??
		visibleDownloads[0]?.url ??
		''
	);
};

const isRichTextRoot = (value: unknown): value is RichTextNode =>
	Boolean(
		value &&
		typeof value === 'object' &&
		'type' in value &&
		'children' in value &&
		Array.isArray((value as { children?: unknown }).children),
	);

const getRichTextRoot = (richText?: RichText): RichTextNode | undefined => {
	if (!richText || typeof richText !== 'object') {
		return undefined;
	}

	if (isRichTextRoot(richText)) {
		return richText;
	}

	const directRoot = (richText as { root?: unknown }).root;
	if (isRichTextRoot(directRoot)) {
		return directRoot;
	}

	const valueRoot = (richText as { value?: { root?: unknown } }).value?.root;
	if (isRichTextRoot(valueRoot)) {
		return valueRoot;
	}

	const localizedRoot = Object.values(richText).find((value) =>
		isRichTextRoot((value as { root?: unknown })?.root),
	) as { root?: RichTextNode } | undefined;
	return localizedRoot?.root;
};

const getImageUrl = (image?: Image): string | undefined => {
	if (!image) {
		return undefined;
	}

	if (typeof image === 'string') {
		return image;
	}

	return image.assetJson?.imageUrl ?? image.url ?? image.path ?? image.src;
};

const groupVersions = (downloads: ProductVersion[]): MajorVersionGroup[] => {
	const majorGroups = new Map<string, Map<string, ProductVersion[]>>();

	downloads.forEach((download) => {
		if (!majorGroups.has(download.majorVersion)) {
			majorGroups.set(download.majorVersion, new Map<string, ProductVersion[]>());
		}

		const minorGroups = majorGroups.get(download.majorVersion);
		if (!minorGroups?.has(download.minorVersion)) {
			minorGroups?.set(download.minorVersion, []);
		}

		minorGroups?.get(download.minorVersion)?.push(download);
	});

	return Array.from(majorGroups.entries())
		.sort(([a], [b]) => compareVersions(a, b))
		.map(([majorVersion, minorMap]) => ({
			majorVersion,
			minorVersions: Array.from(minorMap.entries())
				.sort(([a], [b]) => compareVersions(a, b))
				.map(([minorVersion, versions]) => ({
					minorVersion,
					versions: versions.sort((a, b) => compareVersions(a.longVersion, b.longVersion)),
				})),
		}));
};

const pageStyle = css({
	paddingBlock: token('space.500'),
	color: wacToken('color.extended.blue1000'),
	fontFamily:
		'Charlie Text, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Noto Sans, Ubuntu, Droid Sans, Helvetica Neue, sans-serif',
});

const emptyShellCss = css({
	textAlign: 'center',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	minHeight: '500px',
});

const latestCardsStyle = css({
	display: 'flex',
	justifyContent: 'center',
	gap: '24px',
	[media.below.sm]: {
		flexDirection: 'column',
		alignItems: 'center',
	},
});

const latestCardStyle = css({
	width: '400px',
	padding: '32px',
	border: `1px solid ${token('color.border', '#EBECF0')}`,
	borderRadius: '8px',
	backgroundColor: wacToken('color.neutral.light0'),
	boxShadow: token('elevation.shadow.overlay'),
	textAlign: 'center',
	maxWidth: '50%',
	[media.below.sm]: {
		maxWidth: '100%',
		alignItems: 'center',
	},
});

const eyebrowStyle = css({
	margin: 0,
	color: token('color.text.subtle'),
	fontWeight: 600,
	textTransform: 'uppercase',
});

const versionHeadingStyle = css({
	marginBlock: '8px 12px',
});

const enterpriseBadgeStyle = css({
	display: 'inline-flex',
	alignItems: 'center',
	gap: token('space.050'),
	borderBlockEnd: `1px dashed ${wacToken('color.neutral.light400')}`,
	paddingBlockEnd: '2px',
	verticalAlign: 'middle',
});

const enterpriseBadgeHelpStyle = css({
	appearance: 'none',
	border: 0,
	backgroundColor: wacToken('color.neutral.light400'),
	color: wacToken('color.neutral.light0'),
	padding: 0,
	lineHeight: 1,
	width: '16px',
	height: '16px',
	fontFamily: 'inherit',
	fontWeight: 700,
	cursor: 'pointer',
	borderRadius: '50%',
	'&:hover': {
		backgroundColor: wacToken('color.neutral.light500'),
	},
});

const overlayStyle = css({
	position: 'fixed',
	inset: 0,
	zIndex: 9,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	padding: token('space.300'),
	backgroundColor: token('color.blanket'),
});

const modalStyle = css({
	position: 'relative',
	maxWidth: '560px',
	width: '100%',
	padding: token('space.400'),
	borderRadius: '8px',
	backgroundColor: wacToken('color.neutral.light0'),
	boxShadow: token('elevation.shadow.overlay'),
});

const closeButtonStyle = css({
	position: 'absolute',
	insetBlockStart: token('space.150'),
	insetInlineEnd: token('space.150'),
	appearance: 'none',
	border: 0,
	background: 'transparent',
	fontSize: '24px',
	lineHeight: 1,
	cursor: 'pointer',
});

const latestMetaStyle = css({
	marginBlock: '0 20px',
	color: token('color.text.subtle'),
});

const releaseNotesStyle = css({
	display: 'inline-block',
	marginBlockStart: '16px',
	color: wacToken('color.extended.blue700'),
});

const eapNoticeStyle = css({
	marginBlockStart: token('space.300'),
	color: token('color.text.subtle'),

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	p: {
		marginBlock: token('space.050'),
	},
});

const latestEnterpriseSummaryStyle = css({
	marginBlockStart: token('space.500'),
	textAlign: 'center',
});

const majorListStyle = css({
	listStyle: 'none',
	margin: 0,
	padding: 0,
});

const majorItemStyle = css({
	borderBlockStart: `1px solid ${token('color.border', '#EBECF0')}`,
});

const majorHeaderStyle = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: '16px',
	paddingBlock: '18px',
});

const versionMajorMinor = css({
	fontSize: token('space.250'),
});

const tertiaryListStyle = css({
	listStyle: 'none',
	margin: 0,
	paddingBlock: 0,
	paddingInlineStart: token('space.500'),
	paddingInlineEnd: 0,
});

const versionRowCss = css({
	display: 'grid',
	gridTemplateRows: 'auto',
	columnGap: token('space.400'),
	rowGap: token('space.200'),
	alignItems: 'center',
	paddingBlock: '14px',
	borderBlockStart: `1px solid ${token('color.border', '#EBECF0')}`,
	gridTemplateColumns: '2fr max-content auto auto',
	[media.below.md]: {
		gridTemplateColumns: '1fr 1fr',
	},
});

const versionRowLabelCss = css({
	display: 'flex',
	justifyContent: 'start',
	alignItems: 'center',
	flexWrap: 'wrap',
	columnGap: token('space.100'),
	rowGap: token('space.050'),
});

const selectedVersionSlideIn = keyframes({
	from: {
		opacity: 0,
		transform: 'translateX(48px)',
	},
	to: {
		opacity: 1,
		transform: 'translateX(0)',
	},
});

const linkButtonStyle = css({
	appearance: 'none',
	border: 0,
	background: 'transparent',
	padding: 0,
	color: wacToken('color.extended.blue700'),
	font: 'inherit',
	fontWeight: 600,
	textAlign: 'left',
	cursor: 'pointer',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&:hover': {
		textDecoration: 'underline',
	},
});

const statusStyle = css({
	paddingBlock: token('space.200'),
	paddingInline: 0,
});

const agreementStyle = css({
	maxWidth: '720px',
	marginInline: 'auto',
	paddingInline: token('space.400'),
	animation: `${selectedVersionSlideIn} 300ms ${wacToken('motion.easing.quick-entrance')} both`,
	'@media (prefers-reduced-motion: reduce)': {
		animation: 'none',
	},
});

const selectStyle = css({
	display: 'block',
	marginBlock: token('space.200'),
	padding: '8px 12px',
	border: 'none',
	borderRadius: '4px',
	fontFamily: 'inherit',
	fontSize: 'inherit',
	background: wacToken('color.neutral.light200'),
	['&.card']: {
		marginInline: 'auto',
		width: 'min-content',
		maxWidth: '100%',
	},
	['&.post-download']: {
		width: '100%',
	},
});

const checkboxRowStyle = css({
	display: 'flex',
	gap: '8px',
	alignItems: 'flex-start',
	marginBlock: '24px',
});

const agreementActionsStyle = css({
	display: 'flex',
	gap: '16px',
	alignItems: 'center',
	flexWrap: 'wrap',
});

const inlineLinkStyle = css({
	color: wacToken('color.extended.blue700'),
	fontWeight: 700,
});

const chooseAnotherStyle = css({
	appearance: 'none',
	border: 0,
	background: 'transparent',
	padding: 0,
	color: wacToken('color.extended.blue700'),
	cursor: 'pointer',
});

const postDownloadStyle = css({
	paddingBlockStart: token('space.800'),
	textAlign: 'center',
});

const postDownloadImageStyle = css({
	display: 'block',
	width: '60%',
	height: 'auto',
	marginInline: 'auto',
	aspectRatio: '1/1',
});

const postDownloadTextBlock = css({
	paddingBlockEnd: token('space.400'),
	textAlign: 'left',
});

const postDownloadGettingStartedStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	ol: {
		display: 'grid',
		gap: token('space.100'),
		padding: `${token('space.200')} 0`,
		margin: 0,
		listStyle: 'none',
		counterReset: 'getting-started-step',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	li: {
		display: 'grid',
		gridTemplateColumns: '40px 1fr',
		alignItems: 'center',
		gap: token('space.050'),
		counterIncrement: 'getting-started-step',
		fontSize: '20px',
		fontWeight: 600,
		lineHeight: 1.4,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'li::before': {
		content: 'counter(getting-started-step)',
		display: 'inline-grid',
		placeItems: 'center',
		width: '30px',
		height: '30px',
		border: `2px solid ${token('color.border', '#EBECF0')}`,
		borderRadius: '50%',
		backgroundColor: wacToken('color.neutral.light0'),
		color: wacToken('color.extended.blue1000'),
		fontWeight: 700,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	p: {
		fontSize: '20px',
		fontWeight: 600,
		lineHeight: 1.4,
		marginBlock: token('space.100'),
	},
});

const postDownloadResourcesBandStyle = css({
	marginInline: 'calc(50% - 50vw)',
	paddingBlock: token('space.800'),
	backgroundColor: token('color.background.neutral'),
});

const postDownloadResourcesStyle = css({
	maxWidth: '960px',
	marginInline: 'auto',
	paddingInline: token('space.300'),
	textAlign: 'center',
});

const postDownloadResourcesHeadingStyle = css({
	marginBlock: `0 ${token('space.400')}`,
	fontSize: '48px',
	lineHeight: 1.15,
	letterSpacing: '-0.03em',
	[media.below.md]: {
		fontSize: '36px',
	},
});

const postDownloadResourcesRichTextStyle = css({
	fontSize: '20px',
	lineHeight: 1.5,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	ul: {
		display: 'flex',
		justifyContent: 'center',
		flexWrap: 'wrap',
		gap: `${token('space.100')} ${token('space.150')}`,
		margin: 0,
		padding: 0,
		listStyle: 'none',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	li: {
		display: 'inline',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'li + li::before': {
		content: '"|"',
		marginInlineEnd: token('space.150'),
		color: wacToken('color.extended.blue1000'),
	},
});

const installerCtaWrapperStyle = css({
	paddingBlock: token('space.600'),
	textAlign: 'center',
});

/* eslint-disable complexity -- render state branches mirror the legacy update/download flow. */
const UniformDownloadArchives = ({
	productKey,
	editionDisplay = 'standard',
	enterpriseLabelText,
	showMostPopularDownloadFirst,
	filetype,
	showChecksum = false,
	downloadSelectLabelText,
	confirmationHeaderText,
	image,
	imageAltText,
	gettingStartedHeading,
	gettingStartedRichText,
	resourcesRichText,
	downloadsApiBase,
	errorMessage,
}: UniformDownloadArchivesProps) => {
	const intl = useIntl();
	const resolvedProductName = PRODUCT_DISPLAY_NAMES[productKey] ?? productKey;
	const [downloads, setDownloads] = useState<ProductVersion[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [expandedMajorVersions, setExpandedMajorVersions] = useState<Set<string>>(new Set());
	const [selectedVersion, setSelectedVersion] = useState<ProductVersion | undefined>();
	const [selectedDownloadUrl, setSelectedDownloadUrl] = useState('');
	const [featuredDownloadUrls, setFeaturedDownloadUrls] = useState<Record<string, string>>({});
	const [isAgreementDownloadPreselected, setIsAgreementDownloadPreselected] = useState(false);
	const [lastDownloadedUrl, setLastDownloadedUrl] = useState('');
	const [hasAcceptedAgreement, setHasAcceptedAgreement] = useState(false);
	const [isPostDownloadVisible, setIsPostDownloadVisible] = useState(false);
	const [isEnterpriseHelpVisible, setIsEnterpriseHelpVisible] = useState(false);
	const postDownloadImageUrl = getImageUrl(image) ?? DEFAULT_POST_DOWNLOAD_IMAGE_URL;
	const resolvedEnterpriseLabelText =
		enterpriseLabelText || intl.formatMessage(messages.enterpriseLabelText);
	const resolvedConfirmationHeaderText =
		confirmationHeaderText || intl.formatMessage(messages.confirmationHeaderText);
	const resolvedDownloadSelectLabelText =
		downloadSelectLabelText || intl.formatMessage(messages.downloadSelectLabel);
	const resolvedGettingStartedHeading =
		gettingStartedHeading || intl.formatMessage(messages.gettingStartedHeading);
	const defaultGettingStartedSteps = [
		intl.formatMessage(messages.gettingStartedStepLocate),
		intl.formatMessage(messages.gettingStartedStepRun),
	];
	const gettingStartedRichTextRoot = getRichTextRoot(gettingStartedRichText);
	const resourcesRichTextRoot = getRichTextRoot(resourcesRichText);
	const feedUrls = useMemo(
		() => buildFeedUrls({ productKey, downloadsApiBase }),
		[downloadsApiBase, productKey],
	);

	useEffect(() => {
		if (selectedVersion) {
			window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
		}
	}, [selectedVersion]);

	useEffect(() => {
		let isActive = true;

		const fetchDownloads = async () => {
			setIsLoading(true);

			try {
				const feedResults = await Promise.all(
					feedUrls.map(async (feedUrl) => {
						try {
							const response = await fetch(feedUrl, {
								method: 'GET',
								credentials: 'include',
								headers: {
									'Content-Type': 'application/json; charset=UTF-8',
								},
							});

							if (!response.ok) {
								return [];
							}

							const feed = (await response.json()) as unknown;
							return Array.isArray(feed) ? (feed as DownloadFeedItem[]) : [];
						} catch {
							return [];
						}
					}),
				);

				if (!isActive) {
					return;
				}

				const normalizedDownloads = groupDownloadsByVersion(
					feedResults
						.flat()
						.map((item) => {
							try {
								return normalizeFeedItem(item, resolvedProductName, productKey);
							} catch {
								return undefined;
							}
						})
						.filter((download): download is ProductVersion => Boolean(download)),
				);

				setDownloads(normalizedDownloads);
			} finally {
				if (isActive) {
					setIsLoading(false);
				}
			}
		};

		void fetchDownloads();

		return () => {
			isActive = false;
		};
	}, [feedUrls, productKey, resolvedProductName]);

	const latestEnterpriseDownload = downloads.find(isEnterpriseVersion);
	const featuredDownloads = getFeaturedDownloads(downloads, editionDisplay);
	const shouldShowLatestEnterpriseSummary =
		editionDisplay !== 'standard' && latestEnterpriseDownload?.edition === 'Enterprise';
	const versionGroups = useMemo(() => groupVersions(downloads), [downloads]);
	const isEapDownload = isEapProductType(productKey);
	const shouldShowVersionAccordion = !isEapDownload;

	const getFeaturedDownloadKey = (version: ProductVersion, isEnterprise?: boolean): string =>
		`${version.longVersion}-${isEnterprise ? 'enterprise' : 'standard'}`;

	const getDefaultDownloadUrl = (version: ProductVersion): string =>
		findPreferredDownloadUrl({
			version,
			preferredFiletype: filetype,
			showMostPopularDownloadFirst,
		});

	const showAgreementPage = (version: ProductVersion, preselectedDownloadUrl?: string) => {
		setSelectedVersion(version);
		setSelectedDownloadUrl(preselectedDownloadUrl || getDefaultDownloadUrl(version));
		setIsAgreementDownloadPreselected(Boolean(preselectedDownloadUrl));
		setHasAcceptedAgreement(false);
	};

	const showVersionInAccordion = (version: ProductVersion) => {
		setExpandedMajorVersions((current) => new Set(current).add(version.majorVersion));
	};

	const toggleMajorVersion = (majorVersion: string) => {
		setExpandedMajorVersions((current) => {
			const next = new Set(current);
			if (next.has(majorVersion)) {
				next.delete(majorVersion);
			} else {
				next.add(majorVersion);
			}
			return next;
		});
	};

	const showEnterpriseHelp = (event?: MouseEvent<HTMLButtonElement>) => {
		event?.stopPropagation();
		setIsEnterpriseHelpVisible(true);
	};

	const handleDownload = () => {
		if (!selectedDownloadUrl || !hasAcceptedAgreement) {
			return;
		}

		setLastDownloadedUrl(selectedDownloadUrl);
		setIsPostDownloadVisible(true);
		setSelectedVersion(undefined);
		setIsAgreementDownloadPreselected(false);
		setHasAcceptedAgreement(false);
		window.location.href = selectedDownloadUrl;
	};

	const renderDownloadSelector = ({
		id,
		version,
		value,
		onChange,
		isCard,
	}: {
		id: string;
		version: ProductVersion;
		value: string;
		onChange: (value: string) => void;
		isCard: boolean;
	}) => (
		<>
			<label htmlFor={id}>{resolvedDownloadSelectLabelText}</label>
			<select
				className={isCard ? 'card' : 'post-download'}
				css={selectStyle}
				id={id}
				value={value}
				onChange={(event) => onChange(event.target.value)}
			>
				{version.downloads
					.filter((download) => download.show)
					.map((download) => (
						<option
							key={download.url}
							value={download.url}
							data-product-name={version.productName}
							data-product-platform={download.platform}
							data-product-version={version.longVersion}
							data-product-release-date={version.released.toISOString()}
							data-file-name={download.fileName}
							data-file-description={download.description}
							data-file-url={download.url}
						>
							{getDownloadOptionLabel(download)}
						</option>
					))}
			</select>
		</>
	);

	if (isLoading) {
		return (
			<div css={emptyShellCss}>
				<p css={statusStyle}>{intl.formatMessage(messages.loading)}</p>
			</div>
		);
	}

	if (featuredDownloads.length === 0) {
		return (
			<div css={emptyShellCss}>
				<p css={statusStyle}>{errorMessage || intl.formatMessage(messages.noDownloads)}</p>
			</div>
		);
	}

	if (isPostDownloadVisible) {
		return (
			<div css={[pageStyle, postDownloadStyle]}>
				<h2>{resolvedConfirmationHeaderText}</h2>
				<ColumnLayout layout="6-6">
					{postDownloadImageUrl ? (
						<Image
							css={postDownloadImageStyle}
							src={postDownloadImageUrl}
							alt={imageAltText ?? ''}
						/>
					) : null}
					<div css={postDownloadTextBlock}>
						<h3>{resolvedGettingStartedHeading}</h3>
						<div css={postDownloadGettingStartedStyle}>
							{gettingStartedRichTextRoot &&
							!isRichTextValueConsideredEmpty(gettingStartedRichText) ? (
								<RichTextComponent uniformRichText={gettingStartedRichTextRoot} />
							) : (
								<ol>
									{defaultGettingStartedSteps.map((step) => (
										<li key={step}>{step}</li>
									))}
								</ol>
							)}
						</div>
					</div>
				</ColumnLayout>
				{resourcesRichTextRoot && !isRichTextValueConsideredEmpty(resourcesRichText) ? (
					<section css={postDownloadResourcesBandStyle}>
						<div css={postDownloadResourcesStyle}>
							<h3 css={postDownloadResourcesHeadingStyle}>
								{intl.formatMessage(messages.resourcesHeading)}
							</h3>

							<div css={postDownloadResourcesRichTextStyle}>
								<RichTextComponent uniformRichText={resourcesRichTextRoot} />
							</div>
						</div>
					</section>
				) : null}

				<div css={installerCtaWrapperStyle}>
					<Button variant="secondary" colorAgainst="white" href={lastDownloadedUrl}>
						{intl.formatMessage(messages.installerCtaText)}
					</Button>
				</div>
			</div>
		);
	}

	if (selectedVersion) {
		return (
			<div css={[pageStyle, agreementStyle]}>
				<h2>
					{intl.formatMessage(messages.downloadHeading, {
						productName: selectedVersion.productName,
						version: selectedVersion.longVersion,
					})}
				</h2>
				{isAgreementDownloadPreselected
					? null
					: renderDownloadSelector({
							id: 'download-archives-version-select',
							version: selectedVersion,
							value: selectedDownloadUrl,
							onChange: setSelectedDownloadUrl,
							isCard: false,
						})}
				<p>{intl.formatMessage(messages.downloadAgreementDescription)}</p>
				<label css={checkboxRowStyle}>
					<input
						type="checkbox"
						checked={hasAcceptedAgreement}
						onChange={(event) => setHasAcceptedAgreement(event.target.checked)}
					/>
					<span>
						{intl.formatMessage(messages.agreementCheckbox, {
							licenseAgreement: (
								<a
									css={inlineLinkStyle}
									href="https://www.atlassian.com/legal/software-license-agreement"
									target="_blank"
									rel="noopener noreferrer"
								>
									{intl.formatMessage(messages.softwareLicenseAgreement)}
								</a>
							),
							privacyPolicy: (
								<a
									css={inlineLinkStyle}
									href="https://www.atlassian.com/legal/privacy-policy"
									target="_blank"
									rel="noopener noreferrer"
								>
									{intl.formatMessage(messages.privacyPolicy)}
								</a>
							),
						})}
					</span>
				</label>
				<div css={agreementActionsStyle}>
					<Button
						variant="primary"
						type="button"
						onClick={handleDownload}
						isDisabled={!hasAcceptedAgreement || !selectedDownloadUrl}
					>
						{intl.formatMessage(messages.download)}
					</Button>
					<button
						type="button"
						css={chooseAnotherStyle}
						onClick={() => setSelectedVersion(undefined)}
					>
						{intl.formatMessage(messages.chooseAnother)}
					</button>
				</div>
				{showChecksum && selectedDownloadUrl ? (
					<p>
						<a css={inlineLinkStyle} href={`${selectedDownloadUrl}.sha256`}>
							{intl.formatMessage(messages.checksum)}
						</a>
					</p>
				) : null}
			</div>
		);
	}

	return (
		<div css={pageStyle}>
			{isEnterpriseHelpVisible ? (
				<div
					css={overlayStyle}
					role="dialog"
					aria-modal="true"
					aria-labelledby="download-archives-enterprise-help-heading"
				>
					<div css={modalStyle}>
						<button
							type="button"
							css={closeButtonStyle}
							onClick={() => setIsEnterpriseHelpVisible(false)}
							aria-label={intl.formatMessage(messages.enterpriseHelpCloseAriaLabel)}
						>
							×
						</button>
						<h2 id="download-archives-enterprise-help-heading">
							{intl.formatMessage(messages.enterpriseHelpHeading)}
						</h2>
						<p>{intl.formatMessage(messages.enterpriseHelpContent)}</p>
					</div>
				</div>
			) : null}
			<ColumnLayout layout="12">
				<div css={latestCardsStyle}>
					{featuredDownloads.map(({ version, isEnterprise }) => {
						const headingId = `download-archives-${isEnterprise ? 'enterprise' : 'latest'}-heading`;
						const selectorId = `download-archives-${isEnterprise ? 'enterprise' : 'latest'}-select`;
						const featuredDownloadKey = getFeaturedDownloadKey(version, isEnterprise);
						const featuredDownloadUrl =
							featuredDownloadUrls[featuredDownloadKey] || getDefaultDownloadUrl(version);
						const shouldShowDataCenterOnly = shouldShowDataCenterOnlyLozenge(productKey, version);
						return (
							<section
								css={latestCardStyle}
								aria-labelledby={headingId}
								key={`${version.longVersion} - ${isEnterprise ? 'enterprise' : 'standard'}`}
							>
								<p css={eyebrowStyle}>
									{intl.formatMessage(
										isEapDownload
											? messages.eapDownload
											: isEnterprise
												? messages.enterpriseRelease
												: messages.latestRelease,
									)}
								</p>
								<h3 css={versionHeadingStyle} id={headingId}>
									{resolvedProductName} {version.longVersion}
								</h3>
								{shouldShowDataCenterOnly ? (
									<Lozenge
										text={intl.formatMessage(messages.dataCenterOnly)}
										appearance="neutral"
									/>
								) : null}
								<p css={latestMetaStyle}>{version.date}</p>
								{showMostPopularDownloadFirst
									? renderDownloadSelector({
											id: selectorId,
											version,
											value: featuredDownloadUrl,
											onChange: (value) =>
												setFeaturedDownloadUrls((current) => ({
													...current,
													[featuredDownloadKey]: value,
												})),
											isCard: true,
										})
									: null}
								<Button
									variant="primary"
									type="button"
									onClick={() =>
										showAgreementPage(
											version,
											showMostPopularDownloadFirst ? featuredDownloadUrl : undefined,
										)
									}
								>
									{intl.formatMessage(messages.download)}
								</Button>
								{isEapDownload ? (
									<div css={eapNoticeStyle}>
										<p>
											<strong>{intl.formatMessage(messages.eapNotForProductionHeading)}</strong>
										</p>
										<p>{intl.formatMessage(messages.eapNotForProductionDescription)}</p>
									</div>
								) : null}
								{version.releaseNotes ? (
									<div>
										<a
											css={releaseNotesStyle}
											href={version.releaseNotes}
											target="_blank"
											rel="noreferrer"
										>
											{intl.formatMessage(messages.releaseNotes)}
										</a>
									</div>
								) : null}
							</section>
						);
					})}
				</div>
			</ColumnLayout>

			<ColumnLayout layout="10">
				{shouldShowLatestEnterpriseSummary && latestEnterpriseDownload ? (
					<p css={latestEnterpriseSummaryStyle}>
						{intl.formatMessage(messages.latestText)}{' '}
						<span css={enterpriseBadgeStyle} aria-label={resolvedEnterpriseLabelText}>
							<span>({resolvedEnterpriseLabelText})</span>
							<button
								type="button"
								css={enterpriseBadgeHelpStyle}
								onClick={showEnterpriseHelp}
								aria-label={intl.formatMessage(messages.enterpriseHelpButtonAriaLabel)}
							>
								?
							</button>
						</span>
						{' : '}
						<button
							type="button"
							css={linkButtonStyle}
							data-major-version={latestEnterpriseDownload.majorVersion}
							data-minor-version={latestEnterpriseDownload.minorVersion}
							data-version={latestEnterpriseDownload.longVersion}
							onClick={() => showVersionInAccordion(latestEnterpriseDownload)}
						>
							{latestEnterpriseDownload.longVersion}
						</button>
						{' | '}
						{latestEnterpriseDownload.date}
						{' | '}
						{latestEnterpriseDownload.releaseNotes ? (
							<a
								css={linkButtonStyle}
								href={latestEnterpriseDownload.releaseNotes}
								className="release-notes"
								target="_blank"
								rel="noreferrer"
								data-version={latestEnterpriseDownload.longVersion}
								data-edition={latestEnterpriseDownload.edition}
							>
								{intl.formatMessage(messages.releaseNotes)}
							</a>
						) : null}
					</p>
				) : null}
				{shouldShowVersionAccordion ? (
					<section aria-labelledby="download-archives-all-versions-heading">
						<h3 id="download-archives-all-versions-heading">
							{intl.formatMessage(messages.viewAllVersions)}
						</h3>
						<ul css={majorListStyle}>
							{versionGroups.map((majorGroup) => {
								const isExpanded = expandedMajorVersions.has(majorGroup.majorVersion);
								const shouldShowMajorDataCenterOnly = shouldShowMajorDataCenterOnlyLozenge(
									productKey,
									majorGroup.minorVersions.flatMap((minorGroup) => minorGroup.versions),
								);
								return (
									<li css={majorItemStyle} key={majorGroup.majorVersion}>
										<div css={majorHeaderStyle}>
											<div css={versionRowLabelCss}>
												<strong css={versionMajorMinor}>
													{Number(majorGroup.majorVersion).toFixed(1)}
												</strong>
												{shouldShowMajorDataCenterOnly ? (
													<Lozenge
														text={intl.formatMessage(messages.dataCenterOnly)}
														appearance="neutral"
													/>
												) : null}
											</div>
											<button
												type="button"
												css={linkButtonStyle}
												aria-expanded={isExpanded}
												onClick={() => toggleMajorVersion(majorGroup.majorVersion)}
											>
												{intl.formatMessage(
													isExpanded ? messages.hideDetails : messages.showDetails,
												)}
											</button>
										</div>
										{isExpanded ? (
											<ul css={tertiaryListStyle}>
												{majorGroup.minorVersions.map((minorGroup) => (
													<li key={minorGroup.minorVersion}>
														<strong css={versionMajorMinor}>{minorGroup.minorVersion}</strong>
														<ul css={tertiaryListStyle}>
															{minorGroup.versions.map((version) => {
																const shouldShowRowDataCenterOnly = shouldShowDataCenterOnlyLozenge(
																	productKey,
																	version,
																);
																return (
																	<li
																		className="version-row"
																		css={versionRowCss}
																		key={version.longVersion}
																	>
																		<div css={versionRowLabelCss}>
																			<button
																				type="button"
																				css={linkButtonStyle}
																				onClick={() => showAgreementPage(version)}
																			>
																				{version.longVersion}
																			</button>
																			{shouldShowRowDataCenterOnly ? (
																				<Lozenge
																					text={intl.formatMessage(messages.dataCenterOnly)}
																					appearance="neutral"
																				/>
																			) : null}
																			{shouldShowEnterpriseBadge(version) ? (
																				<span
																					css={enterpriseBadgeStyle}
																					aria-label={`${resolvedEnterpriseLabelText} ? `}
																				>
																					<span>({resolvedEnterpriseLabelText})</span>
																					<button
																						type="button"
																						css={enterpriseBadgeHelpStyle}
																						onClick={showEnterpriseHelp}
																						aria-label={intl.formatMessage(
																							messages.enterpriseHelpButtonAriaLabel,
																						)}
																					>
																						?
																					</button>
																				</span>
																			) : null}
																		</div>

																		<span>{version.date}</span>
																		<span>
																			{version.releaseNotes ? (
																				<a
																					css={linkButtonStyle}
																					href={version.releaseNotes}
																					target="_blank"
																					rel="noreferrer"
																				>
																					{intl.formatMessage(messages.releaseNotes)}
																				</a>
																			) : null}
																		</span>
																		<button
																			type="button"
																			css={linkButtonStyle}
																			onClick={() => showAgreementPage(version)}
																		>
																			{intl.formatMessage(messages.download)}
																		</button>
																	</li>
																);
															})}
														</ul>
													</li>
												))}
											</ul>
										) : null}
									</li>
								);
							})}
						</ul>
					</section>
				) : null}
			</ColumnLayout>
		</div>
	);
};
/* eslint-enable complexity */

export default UniformDownloadArchives;
